<script setup>
import ConfirmComponent from '@/components/ConfirmComponent.vue';
import ToastComponent from '@/components/ToastComponent.vue';
import { useAxios } from '@/hooks/useAxios';
import { nextTick, onMounted, onUnmounted, reactive, ref, watch } from 'vue';

const props = defineProps({
    contractNo: {
        type: String,
        default: ''
    },
    contractNoteData: {
        type: Array,
        default: () => []
    },
    title: {
        type: String,
        default: '계약메모'
    },
    disabled: {
        type: Boolean,
        default: false
    }
});

const emit = defineEmits(['update']);
const showConfirmDialog = ref(false);
const toastComponentRef = ref(null);
const confirmAction = ref(null);
const dialogHeader = ref('');
const dialogMessage = ref('');
const dialogcomplete = ref('');
let isConfirming = false;
const deleteNoteId = ref('');

const isEditNote = ref('');
const editingNote = ref('');

const initialCreateData = {
    contract_no: '',
    note: ''
};
const createData = reactive({ ...initialCreateData });

const initialData = [
    {
        contract_note_id: 0,
        reservation_no: '',
        contract_no: '',
        note: '',
        date_time: '',
        writer: ''
    }
];
const noteList = reactive([]);

const resetData = () => {
    Object.assign(noteList, initialData);
    Object.assign(createData, initialCreateData);

    emit('update', structuredClone(initialData));
};

const setDetailData = (newVal = []) => {
    console.log('들어온 계약메모', newVal);
    const list = Array.isArray(newVal) ? newVal : [];
    noteList.splice(
        0,
        noteList.length,
        ...list.map((item) => ({ ...item }))
    );
};

watch(
    () => props.contractNoteData,
    (newVal) => {
        setDetailData(newVal);
    },
    { immediate: true, deep: false }
);

const saveContractNote = async (data) => {
    const config = {
        method: 'post'
    };

    try {
        const { execute } = useAxios('/api/contract/contract-note', config, {
            immediate: false,
            onSuccess(response) {
                console.log(response);
                toastComponentRef.value.showToast('success', '등록', '계약메모가 등록 되었습니다.');
                nextTick(() => {
                    Object.assign(createData, initialCreateData);
                    noteList.splice(0, noteList.length, ...response.data.data);
                });
            },
            onError(err) {
                toastComponentRef.value.showToast('error', '등록 실패', err.message);
            }
        });
        await execute(data);
    } catch (err) {
        console.log(err);
    }
};

const handleClickButton = (event) => {
    if (props.disabled) return;
    createData.contract_no = props.contractNo;
    console.log('왜 요청하는데?');
    saveContractNote(createData);
};

const handleDeleteNote = (id) => {
    setDialogInfo('delete');
    showConfirmDialog.value = true;
    deleteNoteId.value = id;
};

const handleChangeEdit = (id, note) => {
    if (isEditNote.value !== id) {
        isEditNote.value = id;
        editingNote.value = note;
    } else {
        isEditNote.value = '';
        editingNote.value = '';
    }
};

const handleUpdateNote = () => {
    setDialogInfo('update');
    showConfirmDialog.value = true;
};

const setDialogInfo = (action) => {
    confirmAction.value = action;
    dialogHeader.value = dialogInfo[action].header;
    dialogMessage.value = dialogInfo[action].message;
    dialogcomplete.value = dialogInfo[action].completemessage;
};

const dialogInfo = {
    delete: {
        header: '삭제',
        message: '계약 메모를 삭제하시겠습니까?',
        completemessage: '계약 메모가 삭제되었습니다.'
    },
    update: {
        header: '수정',
        message: '계약 메모를 수정하시겠습니까?',
        completemessage: '계약 메모가 수정되었습니다.'
    }
};

const handleConfirm = () => {
    if (isConfirming) return;
    isConfirming = true;

    let url = '';
    let config = null;
    let payload = null;

    switch (confirmAction.value) {
        case 'delete':
            url = `/api/contract/contract-note/`;
            config = {
                method: 'delete'
            };

            try {
                const { execute } = useAxios(`${url}${deleteNoteId.value}?contractNo=${props.contractNo}`, config, {
                    immediate: false,
                    onSuccess(response) {
                        console.log(response);
                        confirmSuccess();
                        nextTick(() => {
                            noteList.splice(0, noteList.length, ...response.data.data);
                        });
                    },
                    onError(err) {
                        toastComponentRef.value.showToast('error', '삭제 실패', err.message);
                    }
                });
                execute();
            } catch (err) {
                console.log(err);
            } finally {
                isConfirming = false;
                deleteNoteId.value = '';
            }

            break;

        case 'update':
            url = `/api/contract/contract-note/`;
            config = {
                method: 'put'
            };

            payload = {
                contract_no: props.contractNo,
                note: editingNote.value
            };

            try {
                const { execute } = useAxios(`${url}${isEditNote.value}`, config, {
                    immediate: false,
                    onSuccess(response) {
                        console.log(response);
                        confirmSuccess();
                        nextTick(() => {
                            noteList.splice(0, noteList.length, ...response.data.data);
                        });
                    },
                    onError(err) {
                        toastComponentRef.value.showToast('error', '수정 실패', err.message);
                    }
                });
                execute(payload);
            } catch (err) {
                console.log(err);
            } finally {
                isConfirming = false;
                isEditNote.value = '';
                editingNote.value = '';
            }
    } // end switch
};

const confirmSuccess = () => {
    if (toastComponentRef.value) {
        toastComponentRef.value.showToast('success', dialogHeader.value, dialogcomplete.value);
    }
    showConfirmDialog.value = false;
    setTimeout(() => {
        isConfirming = false;
    }, 1000);
};

const handleCancel = () => {
    if (isConfirming) return;
    showConfirmDialog.value = false;
};

const windowWidth = ref(window.innerWidth);

const updateWidth = () => {
    windowWidth.value = window.innerWidth;
};

watch(
    () => props.contractNo,
    (newVal) => {
        createData.contract_no = newVal ?? '';
    },
    { immediate: true }
);

onMounted(() => window.addEventListener('resize', updateWidth));
onUnmounted(() => window.removeEventListener('resize', updateWidth));

defineExpose({
    resetData,
    setDetailData
});
</script>
<template>
    <div class="section-box base-text">
        <ToastComponent ref="toastComponentRef" />
        <ConfirmComponent
            v-model:visible="showConfirmDialog"
            :dialogHeader="dialogHeader"
            :dialogMessage="dialogMessage"
            dialogIcon="pi pi-exclamation-triangle"
            dialogWidth="400px"
            dialogButtonSeverity="danger"
            @confirm="handleConfirm"
            @cancel="handleCancel"
        />
        <h2 class="section-title">{{ props.title }}</h2>
        <div class="section-body block mt-4">
            <div class="w-full bg-[#f5f7f8] flex xxl:flex-row flex-col xxl:gap-[1rem] gap-[0.5rem] p-3">
                <div class="flex-1 xxl:h-[160px] h-[200px]">
                    <div class="h-[160px] overflow-y-auto overflow-hidden">
                        <div
                            v-for="note in noteList"
                            :key="note.contract_note_id"
                            :class="['p-2 rounded-lg border mb-2', isEditNote === note.contract_note_id ? 'bg-gray-200 border-gray-300 hover:bg-gray-200' : 'bg-white border-gray-200 hover:bg-gray-50']"
                        >
                            <div class="flex justify-between items-start relative">
                                <div>
                                    <span class="text-[0.9rem] text-gray-600 font-bold mr-2">{{ note.writer }}</span>
                                    <span class="text-[0.8rem] text-gray-400">{{ note.date_time }}</span>
                                </div>
                                <div v-show="note.writer !== '시스템관리자'" class="absolute top-[-0.4rem] right-[0.1rem]">
                                    <i
                                        v-show="isEditNote !== note.contract_note_id"
                                        class="pi pi-pen-to-square edit-icon"
                                        style="font-size: 0.85rem; margin-right: 0.85rem; cursor: pointer"
                                        @click="handleChangeEdit(note.contract_note_id, note.note)"
                                    ></i>
                                    <i v-show="isEditNote === note.contract_note_id" class="pi pi-undo edit-icon" style="font-size: 0.85rem; cursor: pointer" @click="handleChangeEdit(note.contract_note_id, note.note)"></i>
                                    <i v-show="isEditNote !== note.contract_note_id" class="pi pi-times delete-icon" style="font-size: 0.85rem; cursor: pointer" @click="handleDeleteNote(note.contract_note_id)"></i>
                                </div>
                            </div>
                            <div v-show="isEditNote !== note.contract_note_id">
                                <p class="text-gray-500 mt-1 text-[0.875rem]">{{ note.note }}</p>
                            </div>
                            <div v-show="isEditNote === note.contract_note_id" class="relative flex mt-1 gap-2">
                                <input type="text" v-model="editingNote" class="w-full text-[0.875rem] p-2 bg-white border border-gray-300 rounded-md focus:ring-1 focus:ring-[#3868ff] focus:outline-none" />
                                <button class="text-[#4b5563] py-[2px] px-3 rounded-md bg-[#f1f1f1] hover:bg-[#dddee0] font-semibold absolute right-2 top-1/2 -translate-y-1/2 z-10" @click="handleUpdateNote()">
                                    <i class="pi pi-save"></i>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="flex-1 flex flex-col xxl:h-[164px] h-[200px]">
                    <customtextbox input="note" v-model="createData.note" :height="'118px'" placeholder="메모를 입력해주세요." label="empty" :disabled="props.disabled" />
                    <div class="flex justify-end items-end mt-3 xxl:mt-3">
                        <button
                            :class="[
                                'h-[2.3rem] w-[6rem] border text-[0.9rem] rounded-[0.4rem]',
                                props.disabled ? 'bg-[#f5f5f5] border-[#d1d5db] text-[#9a9ca0] cursor-default' : 'border-blue-600 bg-blue-600 text-white hover:bg-blue-700 active:bg-blue-900 transition-colors duration-150'
                            ]"
                            @click="handleClickButton"
                        >
                            입&nbsp;&nbsp;력
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
<style src="@/assets/css/form-common.css"></style>
<style scoped>
.edit-icon:hover {
    color: #3868ff;
}
.delete-icon:hover {
    color: red;
}
</style>
